import { Box, Typography } from '@mui/material';
import React, {
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useMenuContext } from './MenuContext';
import { MenuGroupContext, useMenuGroupContext } from './MenuGroupContext';
import { MENU_ITEM_STYLE, MenuParentContext } from './menu.model';

export type MenuGroupProps = {
  label?: string;
  children: ReactNode;
};

export const MenuGroup: React.FC<MenuGroupProps> = ({ label, children }) => {
  const { searchString, itemVisibility, registerItemVisibility } =
    useMenuContext();
  const parentContext = useMenuGroupContext();
  const [childIndices, setChildIndices] = useState<Set<number>>(new Set());
  const [hasVisibleChildren, setHasVisibleChildren] = useState(true);

  // Bubble up child index registration to parent (group/submenu) or root
  const registerChildIndex = useCallback(
    (index: number) => {
      setChildIndices((prev) =>
        prev.has(index) ? prev : new Set(prev).add(index),
      );
      // Bubble up to parent context or root
      parentContext?.registerChildIndex(index);
    },
    [parentContext],
  );

  const unregisterChildIndex = useCallback(
    (index: number) => {
      setChildIndices((prev) => {
        if (!prev.has(index)) return prev;
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
      // Bubble up to parent context or root
      parentContext?.unregisterChildIndex(index);
    },
    [parentContext],
  );

  // Bubble up visibility registration to parent (group/submenu) or root
  const handleRegisterItemVisibility = useCallback(
    (itemIndex: number, isVisible: boolean) => {
      // Bubble up to parent context if it exists, otherwise register with root
      if (parentContext) {
        parentContext.registerItemVisibility(itemIndex, isVisible);
      } else {
        registerItemVisibility(itemIndex, isVisible);
      }
    },
    [registerItemVisibility, parentContext],
  );

  const groupContextValue = useMemo<MenuParentContext>(
    () => ({
      registerItemVisibility: handleRegisterItemVisibility,
      registerChildIndex,
      unregisterChildIndex,
    }),
    [handleRegisterItemVisibility, registerChildIndex, unregisterChildIndex],
  );

  // Check if any children are visible by checking registered visibility states
  useEffect(() => {
    if (!searchString) {
      setHasVisibleChildren(true);
      return;
    }

    // Check if any registered child is visible
    // Only consider visible if explicitly set to true (not false, not undefined)
    const hasVisible = Array.from(childIndices).some(
      (index) => itemVisibility.get(index) === true,
    );

    setHasVisibleChildren(hasVisible);
  }, [searchString, itemVisibility, childIndices]);

  if (!hasVisibleChildren) return null;

  return (
    <MenuGroupContext.Provider value={groupContextValue}>
      {label && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: MENU_ITEM_STYLE.MIN_HEIGHT,
            py: MENU_ITEM_STYLE.PADDING_Y,
            px: MENU_ITEM_STYLE.PADDING_X,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>
        </Box>
      )}
      {children}
    </MenuGroupContext.Provider>
  );
};

MenuGroup.displayName = 'MenuGroup';
